import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportCard = ({ 
  reportType, 
  title, 
  description, 
  estimatedSize, 
  sampleColumns = [],
  onGenerate,
  isGenerating = false,
  className = '' 
}) => {
  const [selectedFormat, setSelectedFormat] = useState('csv');
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [selectedColumns, setSelectedColumns] = useState(sampleColumns?.map(col => col?.key));

  const formatOptions = [
    { value: 'csv', label: 'CSV Format (.csv)' },
    { value: 'xlsx', label: 'Excel Format (.xlsx)' },
    { value: 'json', label: 'JSON Format (.json)' }
  ];

  const dateRangeOptions = [
    { value: 'all', label: 'All Data' },
    { value: 'last7', label: 'Last 7 Days' },
    { value: 'last30', label: 'Last 30 Days' },
    { value: 'last90', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const handleGenerate = () => {
    if (onGenerate) {
      onGenerate({
        reportType,
        format: selectedFormat,
        includeHeaders,
        dateRange,
        selectedColumns
      });
    }
  };

  const toggleColumn = (columnKey) => {
    setSelectedColumns(prev => 
      prev?.includes(columnKey) 
        ? prev?.filter(key => key !== columnKey)
        : [...prev, columnKey]
    );
  };

  const getReportIcon = () => {
    switch (reportType) {
      case 'best_price': return 'TrendingDown';
      case 'all_groups': return 'Grid3X3';
      case 'ambiguous': return 'AlertTriangle';
      case 'unmatched': return 'FileX';
      default: return 'FileText';
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-all duration-200 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
            <Icon name={getReportIcon()} size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">Est. size: {estimatedSize}</p>
          </div>
        </div>
        <div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
          {reportType}
        </div>
      </div>
      {/* Description */}
      <p className="text-sm text-muted-foreground mb-6">{description}</p>
      {/* Configuration Options */}
      <div className="space-y-4 mb-6">
        {/* Format Selection */}
        <Select
          label="Export Format"
          options={formatOptions}
          value={selectedFormat}
          onChange={setSelectedFormat}
          className="mb-4"
        />

        {/* Date Range */}
        <Select
          label="Date Range"
          options={dateRangeOptions}
          value={dateRange}
          onChange={setDateRange}
          className="mb-4"
        />

        {/* Include Headers */}
        <Checkbox
          label="Include column headers"
          checked={includeHeaders}
          onChange={(e) => setIncludeHeaders(e?.target?.checked)}
        />
      </div>
      {/* Column Selection */}
      {sampleColumns?.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Select Columns to Include</h4>
          <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
            {sampleColumns?.map((column) => (
              <Checkbox
                key={column?.key}
                label={column?.label}
                checked={selectedColumns?.includes(column?.key)}
                onChange={() => toggleColumn(column?.key)}
                size="sm"
              />
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {selectedColumns?.length} of {sampleColumns?.length} columns selected
          </p>
        </div>
      )}
      {/* Sample Preview */}
      {sampleColumns?.length > 0 && (
        <div className="mb-6 p-3 bg-muted rounded-md">
          <h4 className="text-xs font-medium text-foreground mb-2">Sample Columns:</h4>
          <div className="flex flex-wrap gap-1">
            {sampleColumns?.slice(0, 4)?.map((column) => (
              <span key={column?.key} className="text-xs bg-background px-2 py-1 rounded border">
                {column?.label}
              </span>
            ))}
            {sampleColumns?.length > 4 && (
              <span className="text-xs text-muted-foreground">
                +{sampleColumns?.length - 4} more
              </span>
            )}
          </div>
        </div>
      )}
      {/* Actions */}
      <div className="flex space-x-3">
        <Button
          variant="default"
          onClick={handleGenerate}
          loading={isGenerating}
          iconName="Download"
          iconPosition="left"
          disabled={selectedColumns?.length === 0}
          className="flex-1"
        >
          {isGenerating ? 'Generating...' : 'Generate Report'}
        </Button>
        <Button
          variant="outline"
          iconName="Eye"
          className="px-3"
          title="Preview data"
        />
      </div>
    </div>
  );
};

export default ExportCard;